import { useReducer } from 'react';

import type { PaymentMethod } from '../../_components/payment-method-selector';
import type { SportData } from '../../_components/sport-filter-toggle';
import type { BookingPackage } from '../server/booking-page.loader';
import type {
  AvailableClassData,
  LocationInfo,
  TemplateInfo,
} from '../server/server-actions';

export interface DurationOption {
  duration: number;
  isAvailable: boolean;
  label: string;
}

export interface AvailableCourt {
  id: string;
  name: string;
  name_ar?: string | null;
  isAvailable: boolean;
  type?: string;
  type_ar?: string | null;
  /** Ordered gallery image URLs (courts.image_urls) for the Select-Court card
   *  carousel. Empty/absent → the card renders text-only. */
  image_urls?: string[] | null;
  /** Per-court server-resolved pricing (set when the slot has multiple price tiers). */
  original_price?: number | null;
  final_price?: number | null;
}

export interface SlotCourtInfo {
  id: string;
  name: string;
  name_ar?: string | null;
  type: string;
  type_ar?: string | null;
  /** Gallery image URLs (courts.image_urls), enriched server-side in the
   *  booking-slots service. Drives the Select-Court card carousel. */
  image_urls?: string[] | null;
  template_id?: string;
  original_price?: number | null;
  final_price?: number | null;
}

export interface SlotResourceInfo {
  id: string;
  available_court_ids?: string[];
  name: string;
  bio: string | null;
  picture_url: string | null;
  normal_price: number;
  peak_price: number;
  member_price?: number | null;
  /** Phase B: server-resolved price (peak or normal per this slot). */
  original_price?: number | null;
  /** Phase B: server-resolved final price (member + perk baked in). */
  final_price?: number | null;
  per_person_addon_id?: string | null;
  per_person_price?: number | null;
  max_participants?: number;
  /**
   * Source template_id for this resource. Set during multi-template merge so
   * the reducer can update `selectedTemplateId` when a trainer/ball_machine is
   * picked — needed for accurate package-credit lookup when multiple templates
   * share the same duration (e.g. coach-specific vs. general trainer templates).
   */
  template_id?: string;
}

/** Phase B: template-level membership-perk metadata attached to a slot. */
export interface SlotMemberDiscount {
  addon_id: string;
  type: 'percent' | 'fixed';
  value: number;
}

export interface TimeSlot {
  start_time: string;
  end_time: string;
  is_available: boolean;
  available_court_ids: string[];
  available_courts: SlotCourtInfo[];
  is_peak?: boolean;
  resource_ids?: string[];
  resource_names?: string[];
  available_resources?: SlotResourceInfo[];
  /** Phase B: membership-perk metadata (null when no perk matches). */
  member_discount?: SlotMemberDiscount | null;
  /** Phase B: server-resolved original price (for court bookings; scalar helper). */
  original_price?: number | null;
  /** Phase B: server-resolved final price (for court bookings; scalar helper). */
  final_price?: number | null;
}

/**
 * Booking type — 'court', 'trainer', 'ball_machine', or any class category slug
 * (e.g. 'group_classes', 'kids_academy', 'event', 'tournament').
 */
export type BookingType = string;

const INFRASTRUCTURE_TYPES = new Set(['court', 'trainer', 'ball_machine']);

/** Returns true for booking types that use the class selection flow */
export function isClassType(type: BookingType): boolean {
  return !INFRASTRUCTURE_TYPES.has(type);
}

/**
 * "Generic class" booking types — the default class buckets that fold into the
 * unified "All Classes" pill rather than rendering as a distinct sub-filter.
 * Both `group_classes` and `semi_private` are plain class offerings (a studio
 * may run either as its standard class), so neither should split off into its
 * own chip in unified-class mode. Genuinely distinct categories (event,
 * tournament, kids_academy, the studio's named "Edit" filters) stay as pills.
 */
const GENERIC_CLASS_TYPES = new Set(['group_classes', 'semi_private']);

/** True for class types that roll into "All Classes" (not shown as own pill). */
export function isGenericClassType(type: BookingType): boolean {
  return GENERIC_CLASS_TYPES.has(type);
}

/** Sports that offer `type`; for `'all'`, sports that offer any class type. */
export function getSportsOfferingClassType(
  type: BookingType,
  bookingTypesBySport: Record<string, BookingType[]>,
): string[] {
  return Object.entries(bookingTypesBySport)
    .filter(([, types]) =>
      type === 'all' ? types.some(isClassType) : types.includes(type),
    )
    .map(([sport]) => sport);
}

/**
 * The class view a `/book?type=` link without `?activity=` asks for across
 * sports: `'all'` (every class) or a class category offered by 2+ sports.
 * A category offered by a single sport is just that sport's category, so this
 * returns null and the caller selects that sport instead.
 */
export function getCrossSportClassType(
  presetType: BookingType | undefined,
  presetSport: string | undefined,
  bookingTypesBySport: Record<string, BookingType[]>,
): BookingType | null {
  if (presetSport || !presetType || !isClassType(presetType)) return null;

  const sports = getSportsOfferingClassType(presetType, bookingTypesBySport);
  const minimumSports = presetType === 'all' ? 1 : 2;

  return sports.length >= minimumSports ? presetType : null;
}

export interface SelectedPackage {
  id: string;
  name: string;
  price: number;
  booking_count: number;
  slot_duration?: number;
  validity_months?: number;
  validity_days?: number | null;
  original_price?: number | null;
  package_type?: string;
  player_pricing?: Record<string, number> | null;
  description?: string | null;
  features?: string[];
  color?: string | null;
  max_subscribers?: number | null;
  current_subscribers?: number;
  remaining_slots?: number | null;
  is_bundle?: boolean;
  is_fixed_slot?: boolean;
  location_id?: string | null;
  /** Book-by (false) vs attend-by (true). Must be explicitly false for the
   *  purchase dialog to offer a customer-chosen start date. */
  require_attendance_within_validity?: boolean;
  /** Fixed validity window (tenant-local dates). Shown on the checkout card;
   *  its presence disables the start-date picker (the window wins). */
  fixed_starts_at?: string | null;
  fixed_ends_at?: string | null;
}

export interface HyperpayCheckoutData {
  sessionId: string;
  integrity?: string;
  provider: string;
  /**
   * Optional return-page path override for the frictionless (non-3DS) success
   * callback. Court flow leaves it undefined (dialog defaults to /book/return);
   * the package flow sets it to carry type=package&package_id&purchase_id so the
   * return page reconciles as a package, not a court booking.
   */
  returnPath?: string;
}

export interface MoyasarCheckoutData {
  sessionId: string;
  provider: string;
  bookingId?: string;
  purchaseId?: string;
  participantId?: string;
  isClassPayment?: boolean;
  publishableKey?: string;
  userId?: string;
  playerCount?: number;
  tenantId?: string;
  tenantName?: string;
  locationId?: string;
  packageData?: {
    id: string;
    name: string;
    price: number;
    pricePerPackage?: number;
    booking_count: number;
    validity_months: number;
    validity_days?: number | null;
  };
  // Extra metadata merged into the Moyasar widget's payment metadata (e.g. the
  // package-purchase discount snapshot: discount_code / original_unit_price /
  // discount_acting_user_id + price_per_package) so the return-page/webhook
  // redemption write can persist the user_booking_packages discount columns.
  extraMetadata?: Record<string, string>;
}

interface SelectedBookingAccount {
  id: string;
  name: string;
  isChild: boolean;
}

export interface SelectedAddon {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export interface BookingState {
  // Sport & booking type selection
  sport: string | null;
  bookingType: BookingType;
  /** Classes of `bookingType` are listed for every sport, not just `sport`. */
  allSports: boolean;
  availableSports: string[];
  sportsMetadata: Record<string, SportData>;
  allBookingTypesBySport: Record<string, BookingType[]>;
  allDurationsBySport: Record<string, number[]>;
  allDurationsByBookingType: Record<
    string,
    Partial<Record<BookingType, number[]>>
  >;
  allTemplatesBySport: Record<string, TemplateInfo[]>;
  dynamicSlotSports: Set<string>;

  // Location
  locations: LocationInfo[];
  selectedLocationId: string | null;

  // Booking packages for the currently selected location.
  // Replaces initialData.packages once the user picks a location so the
  // package picker reflects that location's catalog.
  packages: BookingPackage[];

  // Booking details
  date: Date;
  selectedDuration: string;
  selectedTime: string;
  selectedCourt: string;
  selectedTrainer: string;
  trainerFilterId: string;
  selectedBallMachine: string;
  selectedClass: string;
  isSelectedClassFull: boolean;
  isOnWaitlist: boolean;
  isCheckingWaitlist: boolean;
  selectedTemplateId: string;
  selectedBookingAccount: SelectedBookingAccount | null;

  // Fetched data
  timeSlots: TimeSlot[];
  availableCourts: AvailableCourt[];
  availableClasses: AvailableClassData[];
  allClassesForMonth: AvailableClassData[];

  // Set by SELECT_BOOKING_TYPE when the user switches into a class type.
  // SET_ALL_CLASSES_FOR_MONTH consumes it to force-snap the date to the
  // first available class day, so the user doesn't stay on a date that
  // belonged to the previous type's schedule.
  pendingClassDateReset: boolean;

  // Loading states
  initialDataLoaded: boolean;
  isTimeSlotsLoading: boolean;
  isClassesLoading: boolean;
  isCreatingCheckout: boolean;

  // Per-person pricing
  numberOfParticipants: number;
  perPersonPrice: number | null;
  maxParticipants: number;

  // Pricing & membership
  bookingPrice: number | null;
  isPeakTime: boolean;
  isMember: boolean;
  membershipId: string | null;
  membershipLoading: boolean;

  // Payment config
  tenantPublishableKey: string | null;
  /** The tenant's active gateway ('stripe' | 'moyasar' | 'hyperpay' | null).
   *  tenantPublishableKey holds the key for WHICHEVER of these is configured,
   *  so any gateway-specific SDK must gate on this — not on key presence. */
  tenantPaymentProvider: string | null;
  tenantAccountId: string | null;
  enableCashPayment: boolean;
  classPackageCard: boolean;
  enableSplitPayment: boolean;
  maxAdvanceBookingDays: number | null;
  paymentMethod: PaymentMethod;
  isApplePayAvailable: boolean;

  // Package credits
  availableCredits: number;
  hasMatchingDurationPackage: boolean;
  // A matching credit exists but was hidden only because the selected date is
  // outside its attend-by package's validity — drives the "used-by" hint.
  hasDateExcludedCredits: boolean;
  selectedPackage: SelectedPackage | null;

  // Wallet tender — the customer's own wallet balance at this tenant + whether
  // the tenant has the wallet feature enabled (drives the "Pay with Wallet"
  // option, shown only when enabled AND balance covers the full price).
  walletBalance: number;
  walletEnabled: boolean;

  // Guest
  guestUserId: string | null;
  guestEmail: string | null;
  guestName: string | null;
  guestPhone: string | null;

  // Dialogs
  isGuestDrawerOpen: boolean;
  isSuccessDialogOpen: boolean;
  isHyperpayDialogOpen: boolean;
  hyperpayCheckoutData: HyperpayCheckoutData | null;
  isMoyasarDialogOpen: boolean;
  moyasarCheckoutData: MoyasarCheckoutData | null;
  isStripeDialogOpen: boolean;
  stripeClientSecret: string | null;

  // Split payment
  splitPayerCount: number;

  // Customer-facing add-ons. location_ids empty = offered at every location;
  // else the add-on only shows when one of those locations is active. template_ids
  // empty/undefined = offered for every service; else only for bookings on
  // one of those slot_duration_templates.
  availableAddons: Array<{
    id: string;
    name: string;
    price: number;
    category: string;
    location_ids?: string[] | null;
    template_ids?: string[] | null;
    variant_group_id?: string | null;
    variant_label?: string | null;
  }>;
  selectedAddons: Map<string, SelectedAddon>;
  addonsTotalPrice: number;
}

// Fields that must be cleared when the user changes sport, booking type, date, or location.
// Notably excludes `selectedBookingAccount` — the family member / child account
// selection is intentionally sticky across sport/date changes so the user
// doesn't have to re-select it for each booking attempt.
// Also excludes `paymentMethod` (handled per-action), `splitPayerCount`,
// and guest info (retained so the user doesn't need to re-register).
const DOWNSTREAM_RESET: Pick<
  BookingState,
  | 'selectedDuration'
  | 'selectedTime'
  | 'selectedCourt'
  | 'selectedTrainer'
  | 'trainerFilterId'
  | 'selectedBallMachine'
  | 'selectedClass'
  | 'isSelectedClassFull'
  | 'isOnWaitlist'
  | 'isCheckingWaitlist'
  | 'selectedTemplateId'
  | 'availableClasses'
  | 'availableCourts'
  | 'timeSlots'
  | 'bookingPrice'
  | 'isPeakTime'
  | 'numberOfParticipants'
  | 'perPersonPrice'
  | 'maxParticipants'
> = {
  selectedDuration: '',
  selectedTime: '',
  selectedCourt: '',
  selectedTrainer: '',
  trainerFilterId: '',
  selectedBallMachine: '',
  selectedClass: '',
  isSelectedClassFull: false,
  isOnWaitlist: false,
  isCheckingWaitlist: false,
  selectedTemplateId: '',
  availableClasses: [],
  availableCourts: [],
  timeSlots: [],
  bookingPrice: null,
  isPeakTime: false,
  numberOfParticipants: 1,
  perPersonPrice: null,
  maxParticipants: 1,
};

// Fields cleared when the user picks a new time slot.
// Court/trainer/ball machine must be re-selected because available resources
// differ per slot. Price and peak status are recomputed from the new selection.
const TIME_DOWNSTREAM_RESET: Pick<
  BookingState,
  | 'selectedCourt'
  | 'selectedTrainer'
  | 'selectedBallMachine'
  | 'bookingPrice'
  | 'isPeakTime'
  | 'numberOfParticipants'
  | 'perPersonPrice'
  | 'maxParticipants'
> = {
  selectedCourt: '',
  selectedTrainer: '',
  selectedBallMachine: '',
  bookingPrice: null,
  isPeakTime: false,
  numberOfParticipants: 1,
  perPersonPrice: null,
  maxParticipants: 1,
};

type DialogName =
  | 'isGuestDrawerOpen'
  | 'isSuccessDialogOpen'
  | 'isHyperpayDialogOpen'
  | 'isMoyasarDialogOpen'
  | 'isStripeDialogOpen';

export type BookingAction =
  | {
      type: 'SET_PAYMENT_CONTEXT';
      payload: { publishableKey: string | null; provider: string | null };
    }
  | {
      type: 'SET_INITIAL_PACKAGES';
      payload: { locationId: string | null; packages: BookingPackage[] };
    }
  | {
      type: 'INIT_DATA';
      payload: {
        bookingTypes: Record<string, BookingType[]>;
        sports: Record<string, SportData & { dynamicSlotMode?: boolean }>;
        durations?: Record<string, number[]>;
        durationsByBookingType?: Record<
          string,
          Partial<Record<BookingType, number[]>>
        >;
        templates?: Record<string, TemplateInfo[]>;
        locations?: LocationInfo[];
        packages: BookingPackage[];
        isMember: boolean;
        membershipId: string | null;
        publishableKey: string | null;
        provider: string | null;
        accountId: string | null;
        enableCashPayment: boolean;
        classPackageCard: boolean;
        enableSplitPayment: boolean;
        maxAdvanceBookingDays: number | null;
      };
    }
  | {
      type: 'SET_LOCATION_DATA';
      payload: {
        locationId: string;
        bookingTypes: Record<string, BookingType[]>;
        sports: Record<string, SportData & { dynamicSlotMode?: boolean }>;
        durations?: Record<string, number[]>;
        durationsByBookingType?: Record<
          string,
          Partial<Record<BookingType, number[]>>
        >;
        templates?: Record<string, TemplateInfo[]>;
        packages?: BookingPackage[];
      };
    }
  | { type: 'SELECT_SPORT'; payload: string }
  | { type: 'SELECT_BOOKING_TYPE'; payload: BookingType }
  | { type: 'SHOW_CLASSES_ACROSS_SPORTS'; payload: BookingType }
  | { type: 'SELECT_DATE'; payload: Date }
  | { type: 'SELECT_DURATION'; payload: string }
  | {
      type: 'SELECT_SERVICE';
      payload: { templateId: string; durationLabel: string };
    }
  | {
      type: 'SET_TIME_SLOTS';
      payload: { timeSlots: TimeSlot[]; templateId: string };
    }
  | { type: 'SET_TIME_SLOTS_LOADING'; payload: boolean }
  | { type: 'SELECT_TIME'; payload: string }
  | { type: 'SELECT_COURT'; payload: string }
  | { type: 'SELECT_TRAINER_FILTER'; payload: string }
  | {
      type: 'SELECT_TRAINER';
      payload: { trainerId: string; price: number; isPeakTime: boolean };
    }
  | {
      type: 'SELECT_BALL_MACHINE';
      payload: { ballMachineId: string; price: number; isPeakTime: boolean };
    }
  | {
      type: 'SELECT_CLASS';
      payload: {
        classId: string;
        price: number;
        templateId?: string;
        isFull?: boolean;
      };
    }
  | { type: 'SET_ON_WAITLIST'; payload: boolean }
  | { type: 'SET_CLASSES'; payload: AvailableClassData[] }
  | { type: 'SET_ALL_CLASSES_FOR_MONTH'; payload: AvailableClassData[] }
  | { type: 'SET_CLASSES_LOADING'; payload: boolean }
  | {
      type: 'SET_PACKAGE_CREDITS';
      payload: {
        availableCredits: number;
        hasMatchingDurationPackage: boolean;
        hasDateExcludedCredits: boolean;
        numberOfParticipants?: number;
      };
    }
  | {
      type: 'SET_WALLET';
      payload: { walletBalance: number; walletEnabled: boolean };
    }
  | { type: 'SET_PAYMENT_METHOD'; payload: PaymentMethod }
  | {
      type: 'SET_GUEST_INFO';
      payload: {
        guestUserId: string | null;
        guestEmail: string | null;
        guestName: string | null;
        guestPhone: string | null;
      };
    }
  | { type: 'SET_DIALOG'; payload: { dialog: DialogName; open: boolean } }
  | {
      type: 'SET_CHECKOUT_DATA';
      payload:
        | { kind: 'hyperpay'; data: HyperpayCheckoutData | null }
        | { kind: 'moyasar'; data: MoyasarCheckoutData | null }
        | { kind: 'stripe'; data: string | null };
    }
  | { type: 'SET_CREATING_CHECKOUT'; payload: boolean }
  | { type: 'SET_SELECTED_PACKAGE'; payload: SelectedPackage | null }
  | { type: 'SET_BOOKING_ACCOUNT'; payload: SelectedBookingAccount | null }
  | { type: 'SET_SPLIT_PAYER_COUNT'; payload: number }
  | { type: 'SET_PARTICIPANTS'; payload: number }
  | { type: 'SET_APPLE_PAY_AVAILABLE'; payload: boolean }
  | {
      type: 'SET_AVAILABLE_ADDONS';
      payload: Array<{
        id: string;
        name: string;
        price: number;
        category: string;
        location_ids?: string[] | null;
        template_ids?: string[] | null;
      }>;
    }
  | {
      type: 'TOGGLE_ADDON';
      payload: { id: string; name: string; price: number };
    }
  | {
      type: 'SET_ADDON_QUANTITY';
      payload: { addonId: string; quantity: number };
    }
  | { type: 'CLEAR_ADDONS' };

function extractSportsData(
  sports: Record<string, SportData & { dynamicSlotMode?: boolean }>,
) {
  const sportsMetadata: Record<string, SportData> = {};
  const dynamicSlotSports = new Set<string>();

  for (const [slug, data] of Object.entries(sports)) {
    sportsMetadata[slug] = {
      id: data.id,
      slug: data.slug,
      name: data.name,
      name_alt: data.name_alt ?? null,
      icon: data.icon,
      color: data.color,
      enableSplitPayment: data.enableSplitPayment ?? true,
      customerMaxSplitPayers: data.customerMaxSplitPayers ?? 4,
      adminMaxSplitPayers: data.adminMaxSplitPayers ?? 8,
    };
    if (data.dynamicSlotMode) {
      dynamicSlotSports.add(slug);
    }
  }

  return { sportsMetadata, dynamicSlotSports };
}

function formatDateForCompare(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sortCourtsByName(courts: AvailableCourt[]): AvailableCourt[] {
  return [...courts].sort((a, b) => {
    const numA = parseInt(a.name.replace(/\D/g, '')) || 0;
    const numB = parseInt(b.name.replace(/\D/g, '')) || 0;
    return numA - numB;
  });
}

function resolveResourceCourts(
  slot: TimeSlot | undefined,
  resource: SlotResourceInfo | undefined,
  currentCourt: string,
) {
  // Slot RPC order includes split-court occupancy and resource eligibility.
  const prioritizedCourts = (slot?.available_courts ?? [])
    .filter(
      (court) =>
        resource?.available_court_ids === undefined ||
        resource.available_court_ids.includes(court.id),
    )
    .map((court) => ({
      id: court.id,
      name: court.name,
      name_ar: court.name_ar ?? null,
      isAvailable: true,
      type: court.type,
      type_ar: court.type_ar ?? null,
      image_urls: court.image_urls ?? null,
      original_price: court.original_price ?? null,
      final_price: court.final_price ?? null,
    }));

  const availableCourts = sortCourtsByName(prioritizedCourts);

  const selectedCourt = resource
    ? availableCourts.some((court) => court.id === currentCourt)
      ? currentCourt
      : (prioritizedCourts[0]?.id ?? '')
    : availableCourts.length === 1
      ? availableCourts[0]!.id
      : '';

  return { availableCourts, selectedCourt };
}

export function createInitialState(): BookingState {
  return {
    sport: null,
    bookingType: 'court',
    allSports: false,
    availableSports: [],
    sportsMetadata: {},
    allBookingTypesBySport: {},
    allDurationsBySport: {},
    allDurationsByBookingType: {},
    allTemplatesBySport: {},
    dynamicSlotSports: new Set(),

    locations: [],
    selectedLocationId: null,
    packages: [],

    date: new Date(),
    selectedDuration: '',
    selectedTime: '',
    selectedCourt: '',
    selectedTrainer: '',
    trainerFilterId: '',
    selectedBallMachine: '',
    selectedClass: '',
    isSelectedClassFull: false,
    isOnWaitlist: false,
    isCheckingWaitlist: false,
    selectedTemplateId: '',
    selectedBookingAccount: null,

    timeSlots: [],
    availableCourts: [],
    availableClasses: [],
    allClassesForMonth: [],
    pendingClassDateReset: false,

    initialDataLoaded: false,
    isTimeSlotsLoading: false,
    isClassesLoading: false,
    isCreatingCheckout: false,

    numberOfParticipants: 1,
    perPersonPrice: null,
    maxParticipants: 1,

    bookingPrice: null,
    isPeakTime: false,
    isMember: false,
    membershipId: null,
    membershipLoading: false,

    tenantPublishableKey: null,
    tenantPaymentProvider: null,
    tenantAccountId: null,
    enableCashPayment: true,
    classPackageCard: true,
    enableSplitPayment: true,
    maxAdvanceBookingDays: null,
    // Default to card. Apple Pay (Moyasar-only) is promoted to the default
    // by SET_APPLE_PAY_AVAILABLE once the effect confirms both an Apple Pay
    // capable device and a Moyasar tenant; Stripe/Tap/HyperPay keep card.
    paymentMethod: 'card',
    isApplePayAvailable: false,

    availableCredits: 0,
    hasMatchingDurationPackage: false,
    hasDateExcludedCredits: false,
    walletBalance: 0,
    walletEnabled: false,
    selectedPackage: null,

    guestUserId: null,
    guestEmail: null,
    guestName: null,
    guestPhone: null,

    isGuestDrawerOpen: false,
    isSuccessDialogOpen: false,
    isHyperpayDialogOpen: false,
    hyperpayCheckoutData: null,
    isMoyasarDialogOpen: false,
    moyasarCheckoutData: null,
    isStripeDialogOpen: false,
    stripeClientSecret: null,

    splitPayerCount: 4,

    availableAddons: [],
    selectedAddons: new Map(),
    addonsTotalPrice: 0,
  };
}

export function bookingReducer(
  state: BookingState,
  action: BookingAction,
): BookingState {
  const reduced = bookingReducerCore(state, action);
  const sport = reduced.sport ? reduced.sportsMetadata[reduced.sport] : undefined;
  const enabled = sport?.enableSplitPayment ?? true;
  const maxPayers = sport?.customerMaxSplitPayers ?? 4;
  const participantLimit = reduced.bookingType === 'trainer' && reduced.numberOfParticipants > 1
    ? reduced.numberOfParticipants
    : maxPayers;
  const next = {
    ...reduced,
    enableSplitPayment: enabled,
    splitPayerCount: Math.max(2, Math.min(reduced.splitPayerCount, maxPayers, participantLimit)),
    paymentMethod: !enabled && reduced.paymentMethod === 'split'
      ? reduced.isApplePayAvailable ? 'applepay' as const : 'card' as const
      : reduced.paymentMethod,
  };

  // Service-scoped add-ons: whenever the active template changes (service,
  // trainer, ball machine or class selection — many cases set it), drop any
  // selected add-on that is restricted to other templates. Without this a
  // scoped add-on picked earlier would ride into checkout, be charged, and
  // then be rejected by the server-side gate — a price mismatch.
  if (
    next.selectedTemplateId !== state.selectedTemplateId &&
    next.selectedAddons.size > 0
  ) {
    return pruneAddonsForTemplate(next);
  }

  return next;
}

function pruneAddonsForTemplate(state: BookingState): BookingState {
  const scopeByAddonId = new Map(
    state.availableAddons.map((a) => [a.id, a.template_ids ?? []]),
  );

  const pruned = new Map(state.selectedAddons);
  for (const id of pruned.keys()) {
    const scope = scopeByAddonId.get(id);
    // Unknown addon (defensive) or unscoped addon: keep.
    if (!scope || scope.length === 0) continue;
    if (
      !state.selectedTemplateId ||
      !scope.includes(state.selectedTemplateId)
    ) {
      pruned.delete(id);
    }
  }

  if (pruned.size === state.selectedAddons.size) return state;

  return {
    ...state,
    selectedAddons: pruned,
    addonsTotalPrice: computeAddonsTotal(pruned),
  };
}

/**
 * The selected template, looked up beyond the active sport: cross-sport class
 * browsing keeps `state.sport` on the sport the customer came from, so a class
 * picked from another sport only resolves from that sport's templates.
 */
export function findSelectedTemplate(
  state: Pick<
    BookingState,
    'selectedTemplateId' | 'sport' | 'allTemplatesBySport'
  >,
): TemplateInfo | undefined {
  if (!state.selectedTemplateId) return undefined;
  const isSelected = (t: TemplateInfo) => t.id === state.selectedTemplateId;
  const inActiveSport = state.sport
    ? state.allTemplatesBySport[state.sport]?.find(isSelected)
    : undefined;

  return (
    inActiveSport ??
    Object.values(state.allTemplatesBySport).flat().find(isSelected)
  );
}

/**
 * Whether card + Apple Pay must be hidden at checkout, forcing package credits.
 * Two independent sources:
 *  - the selected template's `allow_direct_payment=false` (a per-template policy
 *    that applies to EVERY booking type — court, trainer, ball machine, class);
 *  - the tenant-wide `classPackageCard=false` kill-switch, which is class-only.
 * Mirrors `blockDirectPayment` in booking-form.tsx so the reducer's default
 * payment-method selection never lands on a hidden method.
 */
function isDirectPaymentBlocked(state: BookingState): boolean {
  const selectedTemplate = findSelectedTemplate(state);

  return (
    selectedTemplate?.allow_direct_payment === false ||
    (isClassType(state.bookingType) && !state.classPackageCard)
  );
}

function bookingReducerCore(
  state: BookingState,
  action: BookingAction,
): BookingState {
  switch (action.type) {
    case 'SET_PAYMENT_CONTEXT':
      return {
        ...state,
        tenantPublishableKey: action.payload.publishableKey,
        tenantPaymentProvider: action.payload.provider,
      };

    case 'SET_INITIAL_PACKAGES': {
      const matchesLocation = state.selectedLocationId === action.payload.locationId ||
        (state.selectedLocationId === null && state.locations.length === 1 &&
          state.locations[0]?.id === action.payload.locationId);
      return matchesLocation ? { ...state, packages: action.payload.packages } : state;
    }

    case 'INIT_DATA': {
      const {
        bookingTypes,
        sports,
        durations,
        durationsByBookingType,
        templates,
        locations,
        packages,
      } = action.payload;
      const { sportsMetadata, dynamicSlotSports } = extractSportsData(sports);
      const sportSlugs = Object.keys(bookingTypes);
      const firstSport = sportSlugs[0] ?? null;
      const firstBookingType =
        (firstSport && bookingTypes[firstSport]?.[0]) ?? 'court';

      return {
        ...state,
        allBookingTypesBySport: bookingTypes,
        sportsMetadata,
        dynamicSlotSports,
        allDurationsBySport: durations ?? state.allDurationsBySport,
        allDurationsByBookingType:
          durationsByBookingType ?? state.allDurationsByBookingType,
        allTemplatesBySport: templates ?? state.allTemplatesBySport,
        locations: locations ?? [],
        packages,
        availableSports: sportSlugs,
        sport: firstSport,
        bookingType: firstBookingType,
        isMember: action.payload.isMember,
        membershipId: action.payload.membershipId,
        membershipLoading: false,
        tenantPublishableKey: action.payload.publishableKey,
        tenantPaymentProvider: action.payload.provider,
        tenantAccountId: action.payload.accountId,
        enableCashPayment: action.payload.enableCashPayment,
        classPackageCard: action.payload.classPackageCard,
        enableSplitPayment: action.payload.enableSplitPayment,
        maxAdvanceBookingDays: action.payload.maxAdvanceBookingDays,
        initialDataLoaded: true,
      };
    }

    case 'SET_LOCATION_DATA': {
      const {
        locationId,
        bookingTypes,
        sports,
        durations,
        durationsByBookingType,
        templates,
        packages,
      } = action.payload;
      const { sportsMetadata, dynamicSlotSports } = extractSportsData(sports);
      const sportSlugs = Object.keys(bookingTypes);
      const firstSport = sportSlugs[0] ?? null;

      // Preserve the current sport/type when they still exist at the new
      // location, only falling back to the first when they don't. The init
      // effect applies deep-link presets (?activity=&type=) once and does NOT
      // re-run on a manual location change, so a hard reset to "first" here
      // would silently drop a deep-linked selection — and because a
      // service-level lock hides the booking-type toggle, the customer would be
      // stranded on the wrong service with no way to correct it.
      // A cross-sport class view survives while 2+ sports here offer it (any
      // for 'all'); with one left, it narrows to that sport.
      const offeringSports = state.allSports
        ? getSportsOfferingClassType(state.bookingType, bookingTypes)
        : [];
      const keepAllSports =
        offeringSports.length >= (state.bookingType === 'all' ? 1 : 2);
      const nextSport =
        offeringSports.length === 1 && !keepAllSports
          ? offeringSports[0]!
          : state.sport && sportSlugs.includes(state.sport)
            ? state.sport
            : firstSport;
      const nextSportTypes = (nextSport && bookingTypes[nextSport]) || [];
      const nextBookingType =
        keepAllSports || nextSportTypes.includes(state.bookingType)
          ? state.bookingType
          : (nextSportTypes[0] ?? 'court');

      return {
        ...state,
        selectedLocationId: locationId,
        allBookingTypesBySport: bookingTypes,
        sportsMetadata,
        dynamicSlotSports,
        allDurationsBySport: durations ?? state.allDurationsBySport,
        allDurationsByBookingType:
          durationsByBookingType ?? state.allDurationsByBookingType,
        allTemplatesBySport: templates ?? state.allTemplatesBySport,
        packages: packages ?? state.packages,
        availableSports: sportSlugs,
        sport: nextSport,
        bookingType: nextBookingType,
        allSports: keepAllSports,
        allClassesForMonth: [],
        ...DOWNSTREAM_RESET,
      };
    }

    case 'SELECT_SPORT': {
      const newSport = action.payload;
      const availableTypes = state.allBookingTypesBySport[newSport] || [];
      // Narrowing "All classes" to one sport keeps the customer on classes.
      const fallbackType =
        (state.bookingType === 'all'
          ? availableTypes.find(isClassType)
          : undefined) ?? availableTypes[0];
      const newBookingType = availableTypes.includes(state.bookingType)
        ? state.bookingType
        : (fallbackType ?? 'court');

      // Reset date to today when the booking type implicitly changes
      // from a class type to a non-class type (see SELECT_BOOKING_TYPE)
      const switchingFromClass =
        isClassType(state.bookingType) && !isClassType(newBookingType);
      const newDate = switchingFromClass ? new Date() : state.date;
      // For class types, ask SET_ALL_CLASSES_FOR_MONTH to snap the date to
      // the first available class day for the new sport/type combination.
      const pendingClassDateReset =
        (newSport !== state.sport || state.allSports) &&
        isClassType(newBookingType)
          ? true
          : state.pendingClassDateReset;

      return {
        ...state,
        date: newDate,
        sport: newSport,
        bookingType: newBookingType,
        allSports: false,
        allClassesForMonth: [],
        pendingClassDateReset,
        ...DOWNSTREAM_RESET,
      };
    }

    case 'SELECT_BOOKING_TYPE': {
      const newBookingType = action.payload;
      let newPaymentMethod = state.paymentMethod;

      if (isClassType(newBookingType)) {
        if (!state.classPackageCard) {
          newPaymentMethod = 'package';
        } else if (state.paymentMethod === 'split') {
          newPaymentMethod = 'card';
        }
      }

      // Reset the date to today on any booking-type switch. Two reasons:
      // (1) switching away from a class type can leave us on a future class-only
      // day where courts/trainers have no slots; (2) switching between class
      // types (e.g. Express Edit → Studio Edit) should land on the first day of
      // the new type's schedule, not stick to the previous selection. The
      // class case is finalized in SET_ALL_CLASSES_FOR_MONTH (which auto-snaps
      // to the first available class day) — we set `pendingClassDateReset`
      // here so that snap fires even when today happens to have classes in
      // the new type's schedule.
      const isTypeChange = state.bookingType !== newBookingType;
      const newDate = isTypeChange ? new Date() : state.date;
      const pendingClassDateReset =
        isTypeChange && isClassType(newBookingType)
          ? true
          : state.pendingClassDateReset;

      // Add-ons are scoped by category to the booking type (court vs coaching
      // vs product). On a real type switch, drop prior selections so a
      // now-hidden add-on (e.g. a court extra picked before switching to the
      // coach tab) can't linger in the cart and get charged.
      const addonReset = isTypeChange
        ? {
            selectedAddons: new Map<string, SelectedAddon>(),
            addonsTotalPrice: 0,
          }
        : null;

      return {
        ...state,
        date: newDate,
        bookingType: newBookingType,
        allSports: false,
        paymentMethod: newPaymentMethod,
        allClassesForMonth: [],
        pendingClassDateReset,
        ...DOWNSTREAM_RESET,
        ...addonReset,
      };
    }

    case 'SHOW_CLASSES_ACROSS_SPORTS': {
      const selected = bookingReducerCore(state, {
        type: 'SELECT_BOOKING_TYPE',
        payload: action.payload,
      });

      return { ...selected, allSports: true, pendingClassDateReset: true };
    }

    case 'SELECT_DATE': {
      const newDate = action.payload;
      // The ternary produces `string` without the explicit cast, which is not
      // assignable to the `PaymentMethod` union type.
      const resetPaymentMethod: PaymentMethod = state.isApplePayAvailable
        ? 'applepay'
        : 'card';
      const base: BookingState = {
        ...state,
        date: newDate,
        ...DOWNSTREAM_RESET,
        // Reset payment method — credits will be re-checked by the credits effect
        paymentMethod: resetPaymentMethod,
        // The user explicitly picked a date — don't let an in-flight
        // SET_ALL_CLASSES_FOR_MONTH override it with the first-available snap.
        pendingClassDateReset: false,
      };

      // For class bookings, filter from cached month data instead of re-fetching.
      // Avoids a network round-trip on each date change when month data is loaded.
      if (
        isClassType(state.bookingType) &&
        state.allClassesForMonth.length > 0
      ) {
        const dateStr = formatDateForCompare(newDate);
        base.availableClasses = state.allClassesForMonth.filter(
          (c) => c.booking_date === dateStr,
        );
      }

      return base;
    }

    case 'SELECT_DURATION': {
      // Reuse DOWNSTREAM_RESET to ensure consistency with other reset sites.
      // selectedClass is included in DOWNSTREAM_RESET but is always empty
      // during non-class flows, so spreading it here is safe and idempotent.
      return {
        ...state,
        ...DOWNSTREAM_RESET,
        selectedDuration: action.payload,
      };
    }

    case 'SELECT_SERVICE': {
      // Service-list flow: the customer picks a named template (the "service")
      // directly instead of a bare duration. We pin BOTH the template and its
      // derived duration label in one shot. Setting selectedDuration keeps the
      // existing time-slot effect working unchanged (it keys off duration);
      // pre-setting selectedTemplateId makes that effect fetch slots for this
      // one template, bypassing the same-duration merge that would otherwise
      // drop the service names.
      return {
        ...state,
        ...DOWNSTREAM_RESET,
        selectedDuration: action.payload.durationLabel,
        selectedTemplateId: action.payload.templateId,
      };
    }

    case 'SET_TIME_SLOTS': {
      const next = {
        ...state,
        timeSlots: action.payload.timeSlots,
        selectedTemplateId: action.payload.templateId,
        isTimeSlotsLoading: false,
      };

      if (
        state.bookingType !== 'trainer' &&
        state.bookingType !== 'ball_machine'
      ) {
        return next;
      }

      const filterStillAvailable = next.timeSlots.some(
        (slot) =>
          slot.is_available &&
          slot.available_resources?.some(
            (resource) => resource.id === state.trainerFilterId,
          ),
      );
      next.trainerFilterId = filterStillAvailable ? state.trainerFilterId : '';

      if (!state.selectedTime) return next;

      // Resolve the chosen resource against fresh availability and pricing,
      // retaining the customer's filter independently of their booked staff.
      const selected = bookingReducerCore(
        {
          ...next,
          trainerFilterId: state.trainerFilterId || state.selectedTrainer,
        },
        { type: 'SELECT_TIME', payload: state.selectedTime },
      );
      selected.trainerFilterId = next.trainerFilterId;
      if (
        selected.availableCourts.some(
          (court) => court.id === state.selectedCourt,
        )
      ) {
        selected.selectedCourt = state.selectedCourt;
      }

      if (
        selected.selectedTrainer &&
        selected.selectedTrainer === state.selectedTrainer
      ) {
        return bookingReducerCore(
          { ...selected, numberOfParticipants: state.numberOfParticipants },
          { type: 'SET_PARTICIPANTS', payload: state.numberOfParticipants },
        );
      }

      return selected;
    }

    case 'SET_TIME_SLOTS_LOADING': {
      return {
        ...state,
        isTimeSlotsLoading: action.payload,
      };
    }

    case 'SELECT_TIME': {
      const selectedTime = action.payload;
      const selectedSlot = state.timeSlots.find(
        (slot) => slot.start_time === selectedTime,
      );
      const preferredTrainer =
        state.bookingType === 'trainer' && state.trainerFilterId
          ? selectedSlot?.available_resources?.find(
              (resource) => resource.id === state.trainerFilterId,
            )
          : undefined;

      if (
        (state.bookingType === 'trainer' ||
          state.bookingType === 'ball_machine') &&
        (!selectedSlot?.is_available ||
          (state.trainerFilterId && !preferredTrainer))
      ) {
        return {
          ...state,
          ...TIME_DOWNSTREAM_RESET,
          selectedTime: '',
          availableCourts: [],
        };
      }

      // Auto-select and calculate price when only one option is available
      let bookingPrice: number | null = null;
      let isPeakTime = false;
      let autoSelectedTrainer = '';
      let autoSelectedBallMachine = '';
      let autoPerPersonPrice: number | null = null;
      let autoMaxParticipants = 1;
      const isPeak = selectedSlot?.is_peak ?? false;
      let autoSelectedTemplateId = '';

      const autoResource =
        preferredTrainer ??
        (state.bookingType === 'ball_machine' &&
        selectedTime === state.selectedTime
          ? selectedSlot?.available_resources?.find(
              (resource) => resource.id === state.selectedBallMachine,
            )
          : undefined) ??
        (selectedSlot?.available_resources?.length === 1
          ? selectedSlot.available_resources[0]
          : undefined);
      const { availableCourts: courts, selectedCourt: autoSelectedCourt } =
        resolveResourceCourts(
          selectedSlot,
          state.bookingType === 'court' ? undefined : autoResource,
          state.selectedCourt,
        );

      // Prefer the staff filter, otherwise select the sole available resource.
      if (state.bookingType !== 'court' && autoResource) {
        const resource = autoResource;
        // Phase B: prefer the server-resolved final_price (has member override
        // + perk baked in). Fall back to the legacy member/peak/normal pick
        // for payloads from an older server.
        const price =
          resource.final_price ??
          (state.isMember && resource.member_price != null
            ? resource.member_price
            : isPeak
              ? resource.peak_price
              : resource.normal_price);

        if (state.bookingType === 'trainer') {
          autoSelectedTrainer = resource.id;
          autoPerPersonPrice = resource.per_person_price ?? null;
          autoMaxParticipants = resource.max_participants ?? 1;
        } else if (state.bookingType === 'ball_machine') {
          autoSelectedBallMachine = resource.id;
        }
        bookingPrice = price;
        isPeakTime = isPeak;
        // Resource-level template_id wins over the duration-level fallback so
        // package credits resolve against the template the picked resource
        // actually belongs to (matters when multiple trainer templates share
        // the same duration).
        if (resource.template_id) {
          autoSelectedTemplateId = resource.template_id;
        }
      }

      // For court bookings: when exactly one court is available it is auto-selected
      // here (same logic as SELECT_COURT) so pricing shows without an extra click.
      // Use the court's tagged template_id for accurate pricing; fall back to a
      // duration-based lookup for slots that predate the template_id tagging.
      if (autoSelectedCourt && state.bookingType === 'court' && state.sport) {
        const autoSelectedCourtInfo = selectedSlot?.available_courts?.find(
          (c) => c.id === autoSelectedCourt,
        );
        const sportTemplates = state.allTemplatesBySport[state.sport] || [];
        const durationValue = parseDurationFromLabel(state.selectedDuration);

        // template_id on the court is set by the multi-template merge in the
        // effects hook. The fallback handles legacy data / single-template tenants.
        const template = autoSelectedCourtInfo?.template_id
          ? sportTemplates.find(
              (t) => t.id === autoSelectedCourtInfo.template_id,
            )
          : sportTemplates.find(
              (t) => t.duration === durationValue && t.has_court_booking,
            );

        if (template) {
          // Per-court price wins when the dynamic RPC differentiates courts
          // by price tier. Fall through to the slot-level Phase B price and
          // finally the legacy template-based pick for older payloads.
          bookingPrice =
            autoSelectedCourtInfo?.final_price ??
            selectedSlot?.final_price ??
            (state.isMember && template.member_price != null
              ? template.member_price
              : isPeak
                ? template.peak_price
                : template.normal_price);
          isPeakTime = isPeak;
          autoSelectedTemplateId = template.id;
        }
      }

      return {
        ...state,
        ...TIME_DOWNSTREAM_RESET,
        selectedTime,
        availableCourts: courts,
        selectedCourt: autoSelectedCourt,
        selectedTrainer: autoSelectedTrainer,
        selectedBallMachine: autoSelectedBallMachine,
        bookingPrice,
        isPeakTime,
        perPersonPrice: autoPerPersonPrice,
        maxParticipants: autoMaxParticipants,
        ...(autoSelectedTemplateId
          ? { selectedTemplateId: autoSelectedTemplateId }
          : {}),
      };
    }

    case 'SELECT_COURT': {
      if (
        (state.bookingType === 'trainer' ||
          state.bookingType === 'ball_machine') &&
        action.payload &&
        !state.availableCourts.some((court) => court.id === action.payload)
      ) {
        return state;
      }

      const newState: BookingState = {
        ...state,
        selectedCourt: action.payload,
      };

      if (
        state.bookingType === 'court' &&
        action.payload &&
        state.selectedTime &&
        state.sport
      ) {
        const selectedSlot = state.timeSlots.find(
          (slot) => slot.start_time === state.selectedTime,
        );
        const isPeak = selectedSlot?.is_peak ?? false;

        // Retrieve the court's tagged template_id so pricing resolves to the
        // correct template when multiple templates share the same duration.
        const selectedCourtInfo = selectedSlot?.available_courts?.find(
          (c) => c.id === action.payload,
        );

        const sportTemplates = state.allTemplatesBySport[state.sport] || [];
        const durationValue = parseDurationFromLabel(state.selectedDuration);

        // template_id on the court is set by the multi-template merge in the
        // effects hook. The fallback handles legacy data / single-template tenants.
        const template = selectedCourtInfo?.template_id
          ? sportTemplates.find((t) => t.id === selectedCourtInfo.template_id)
          : sportTemplates.find(
              (t) => t.duration === durationValue && t.has_court_booking,
            );

        if (template) {
          // Per-court price takes precedence (set by the dynamic RPC when
          // courts in the same slot price differently). Fall back to the
          // slot-level Phase B price, then the client-side template pick.
          const price =
            selectedCourtInfo?.final_price ??
            selectedSlot?.final_price ??
            (state.isMember && template.member_price != null
              ? template.member_price
              : isPeak
                ? template.peak_price
                : template.normal_price);
          newState.bookingPrice = price;
          newState.isPeakTime = isPeak;
          newState.selectedTemplateId = template.id;
        }
      }

      return newState;
    }

    case 'SELECT_TRAINER_FILTER': {
      if (
        state.bookingType !== 'trainer' ||
        action.payload === state.trainerFilterId
      )
        return state;
      if (
        action.payload &&
        !state.timeSlots.some(
          (slot) =>
            slot.is_available &&
            slot.available_resources?.some(
              (resource) => resource.id === action.payload,
            ),
        )
      ) {
        return state;
      }

      return bookingReducerCore(
        { ...state, trainerFilterId: action.payload },
        { type: 'SELECT_TIME', payload: state.selectedTime },
      );
    }

    case 'SELECT_TRAINER': {
      const { trainerId, price, isPeakTime: isPeak } = action.payload;
      // Look up per-person pricing from the selected trainer's resource info
      const selectedSlot = state.timeSlots.find(
        (s) => s.start_time === state.selectedTime,
      );
      const trainerResource = selectedSlot?.available_resources?.find(
        (r) => r.id === trainerId,
      );
      const perPersonPrice = trainerResource?.per_person_price ?? null;
      const maxParticipants = trainerResource?.max_participants ?? 1;

      return {
        ...state,
        ...resolveResourceCourts(
          selectedSlot,
          trainerResource,
          state.selectedCourt,
        ),
        selectedTrainer: trainerId,
        bookingPrice: price,
        isPeakTime: isPeak,
        numberOfParticipants: 1,
        perPersonPrice,
        maxParticipants,
        // The picked trainer carries the template_id it was registered under
        // (set during the multi-template merge in use-booking-effects). Update
        // selectedTemplateId so the package-credit lookup matches packages
        // bound to that template — without this, tenants with multiple trainer
        // templates of the same duration would always check against whichever
        // template happened to be merged first.
        ...(trainerResource?.template_id
          ? { selectedTemplateId: trainerResource.template_id }
          : {}),
      };
    }

    case 'SET_PARTICIPANTS': {
      // Clamp to valid range. The UI buttons also enforce this, but this
      // guards against programmatic dispatch with out-of-range values.
      const participants = Math.min(
        Math.max(1, action.payload),
        state.maxParticipants,
      );
      const extraPeople = Math.max(0, participants - 1);
      const perPersonFee = state.perPersonPrice
        ? extraPeople * state.perPersonPrice
        : 0;

      // We recalculate the base price from scratch on every participant change
      // rather than incrementing/decrementing state.bookingPrice. This prevents
      // accumulated floating-point drift and ensures member/peak pricing
      // is always correctly applied as the base before adding the per-person fee.
      const slot = state.timeSlots.find(
        (s) => s.start_time === state.selectedTime,
      );
      const resource = slot?.available_resources?.find(
        (r) => r.id === state.selectedTrainer,
      );
      let basePrice = state.bookingPrice ?? 0;
      if (resource) {
        // Phase B: resource.final_price is the server-resolved price
        // (member override + perk baked in). Fall back to the legacy pick.
        basePrice =
          resource.final_price ??
          (state.isMember && resource.member_price != null
            ? resource.member_price
            : state.isPeakTime
              ? resource.peak_price
              : resource.normal_price);
      }

      return {
        ...state,
        numberOfParticipants: participants,
        ...(participants !== state.numberOfParticipants &&
        !isClassType(state.bookingType)
          ? {
              availableCredits: 0,
              hasMatchingDurationPackage: false,
              ...(state.paymentMethod === 'package'
                ? {
                    paymentMethod: isDirectPaymentBlocked(state)
                      ? state.enableCashPayment
                        ? ('cash' as const)
                        : ('package' as const)
                      : state.isApplePayAvailable
                        ? ('applepay' as const)
                        : ('card' as const),
                  }
                : {}),
            }
          : {}),
        bookingPrice: basePrice + perPersonFee,
        // If split was selected but we dropped to 1 participant, switch to applepay
        ...(participants <= 1 &&
        state.perPersonPrice != null &&
        state.paymentMethod === 'split'
          ? {
              paymentMethod: state.isApplePayAvailable
                ? ('applepay' as const)
                : ('card' as const),
            }
          : {}),
      };
    }

    case 'SELECT_BALL_MACHINE': {
      const selectedSlot = state.timeSlots.find(
        (s) => s.start_time === state.selectedTime,
      );
      const resource = selectedSlot?.available_resources?.find(
        (r) => r.id === action.payload.ballMachineId,
      );
      return {
        ...state,
        ...resolveResourceCourts(selectedSlot, resource, state.selectedCourt),
        selectedBallMachine: action.payload.ballMachineId,
        bookingPrice: action.payload.price,
        isPeakTime: action.payload.isPeakTime,
        // Same rationale as SELECT_TRAINER: pin selectedTemplateId to the
        // resource's source template so package-credit lookup is correct when
        // multiple ball_machine templates share the same duration.
        ...(resource?.template_id
          ? { selectedTemplateId: resource.template_id }
          : {}),
      };
    }

    case 'SELECT_CLASS': {
      const isFull = action.payload.isFull ?? false;
      return {
        ...state,
        selectedClass: action.payload.classId,
        isSelectedClassFull: isFull,
        isOnWaitlist: false,
        isCheckingWaitlist: isFull,
        bookingPrice: action.payload.price,
        selectedTemplateId:
          action.payload.templateId ?? state.selectedTemplateId,
      };
    }

    case 'SET_ON_WAITLIST': {
      return {
        ...state,
        isOnWaitlist: action.payload,
        isCheckingWaitlist: false,
      };
    }

    case 'SET_CLASSES': {
      return {
        ...state,
        availableClasses: action.payload,
        isClassesLoading: false,
      };
    }

    case 'SET_ALL_CLASSES_FOR_MONTH': {
      const allClasses = action.payload;
      const currentDateStr = formatDateForCompare(state.date);
      const classesForCurrentDate = allClasses.filter(
        (c) => c.booking_date === currentDateStr,
      );

      // Snap to the first available class day when:
      // (a) the user just switched class booking types (`pendingClassDateReset`)
      //     so the date doesn't stick to a day from the previous type's
      //     schedule even if today happens to have classes in the new one; or
      // (b) the currently selected date has no classes at all.
      // Relies on get_available_classes_for_date returning rows ordered by
      // (booking_date, start_time) ASC — so allClasses[0] is the earliest class.
      // The 'T00:00:00' suffix forces local-time Date construction, preventing
      // UTC midnight from shifting the date to the previous day in UTC+N timezones.
      const shouldJumpToFirstAvailable =
        allClasses.length > 0 &&
        (state.pendingClassDateReset || classesForCurrentDate.length === 0);

      let effectiveDate = state.date;
      let effectiveClasses = classesForCurrentDate;
      if (shouldJumpToFirstAvailable) {
        const firstAvailableDate = allClasses[0]!.booking_date;
        effectiveDate = new Date(firstAvailableDate + 'T00:00:00');
        effectiveClasses = allClasses.filter(
          (c) => c.booking_date === firstAvailableDate,
        );
      }

      return {
        ...state,
        allClassesForMonth: allClasses,
        availableClasses: effectiveClasses,
        date: effectiveDate,
        pendingClassDateReset: false,
        isClassesLoading: false,
      };
    }

    case 'SET_CLASSES_LOADING': {
      return {
        ...state,
        isClassesLoading: action.payload,
      };
    }

    case 'SET_PACKAGE_CREDITS': {
      if (
        !isClassType(state.bookingType) &&
        action.payload.numberOfParticipants !== undefined &&
        action.payload.numberOfParticipants !== state.numberOfParticipants
      ) {
        return state;
      }
      const {
        availableCredits,
        hasMatchingDurationPackage,
        hasDateExcludedCredits,
      } = action.payload;
      let newPaymentMethod = state.paymentMethod;

      if (state.bookingPrice === 0) {
        // Free slot (e.g. a complimentary/0-price template): there is nothing
        // to collect, so credits are irrelevant and the "buy a package" force
        // below must not apply. Complimentary is an internal gateway-free path,
        // distinct from recording money as cash.
        newPaymentMethod = 'complimentary';
      } else if (availableCredits > 0 && hasMatchingDurationPackage) {
        // Auto-select package payment when the user has applicable credits.
        // This overrides any previous manual selection, which is intentional:
        // credits are the preferred (cheapest) payment method.
        newPaymentMethod = 'package';
      } else if (isDirectPaymentBlocked(state)) {
        // Direct payment (card/Apple Pay) is hidden for this booking — the
        // template opted out, or the tenant's class kill-switch is off. With no
        // usable credits, land on cash if the tenant offers it (still a valid
        // way to pay), otherwise force 'package' so the "buy a package" prompt
        // shows. Applies to court/trainer/ball-machine too, not just classes.
        newPaymentMethod = state.enableCashPayment ? 'cash' : 'package';
      } else {
        // No applicable credits: fall back to Apple Pay if available, else card.
        newPaymentMethod = state.isApplePayAvailable ? 'applepay' : 'card';
      }

      return {
        ...state,
        availableCredits,
        hasMatchingDurationPackage,
        hasDateExcludedCredits,
        paymentMethod: newPaymentMethod,
      };
    }

    case 'SET_WALLET': {
      // Verbatim: record the customer's wallet balance + tenant feature flag.
      // Does NOT auto-select wallet (unlike package credits) — the customer
      // opts in explicitly; wallet is money, package credits are prepaid.
      return {
        ...state,
        walletBalance: action.payload.walletBalance,
        walletEnabled: action.payload.walletEnabled,
      };
    }

    case 'SET_PAYMENT_METHOD': {
      return {
        ...state,
        paymentMethod: action.payload,
      };
    }

    case 'SET_GUEST_INFO': {
      return {
        ...state,
        guestUserId: action.payload.guestUserId,
        guestEmail: action.payload.guestEmail,
        guestName: action.payload.guestName,
        guestPhone: action.payload.guestPhone,
      };
    }

    case 'SET_DIALOG': {
      return {
        ...state,
        [action.payload.dialog]: action.payload.open,
      };
    }

    case 'SET_CHECKOUT_DATA': {
      if (action.payload.kind === 'hyperpay') {
        return {
          ...state,
          hyperpayCheckoutData: action.payload.data,
        };
      }
      if (action.payload.kind === 'stripe') {
        return {
          ...state,
          stripeClientSecret: action.payload.data,
        };
      }
      return {
        ...state,
        moyasarCheckoutData: action.payload.data,
      };
    }

    case 'SET_CREATING_CHECKOUT': {
      return {
        ...state,
        isCreatingCheckout: action.payload,
      };
    }

    case 'SET_SELECTED_PACKAGE': {
      return {
        ...state,
        selectedPackage: action.payload,
      };
    }

    case 'SET_BOOKING_ACCOUNT': {
      return {
        ...state,
        ...(isClassType(state.bookingType) &&
        state.selectedBookingAccount?.id !== action.payload?.id
          ? {
              ...DOWNSTREAM_RESET,
              allClassesForMonth: [],
              isClassesLoading: true,
            }
          : {}),
        selectedBookingAccount: action.payload,
      };
    }

    case 'SET_SPLIT_PAYER_COUNT': {
      return {
        ...state,
        splitPayerCount: action.payload,
      };
    }

    case 'SET_APPLE_PAY_AVAILABLE': {
      const isAvailable = action.payload;
      let newPaymentMethod = state.paymentMethod;

      if (!isAvailable && state.paymentMethod === 'applepay') {
        // Provider/device can't do native Apple Pay — fall back to card.
        newPaymentMethod = 'card';
      } else if (isAvailable && state.paymentMethod === 'card') {
        // Apple Pay confirmed available (Moyasar + Apple device). Promote it to
        // the default over the initial 'card'. The user can still pick card.
        newPaymentMethod = 'applepay';
      }

      return {
        ...state,
        isApplePayAvailable: isAvailable,
        paymentMethod: newPaymentMethod,
      };
    }

    case 'SET_AVAILABLE_ADDONS': {
      return { ...state, availableAddons: action.payload };
    }

    case 'TOGGLE_ADDON': {
      const { id, name, price } = action.payload;
      const next = new Map(state.selectedAddons);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, { id, name, price, quantity: 1 });
      }

      return {
        ...state,
        selectedAddons: next,
        addonsTotalPrice: computeAddonsTotal(next),
      };
    }

    case 'SET_ADDON_QUANTITY': {
      const { addonId, quantity } = action.payload;
      const next = new Map(state.selectedAddons);

      if (quantity <= 0) {
        // Decrementing to zero or below removes the addon entirely
        next.delete(addonId);
      } else {
        const existing = next.get(addonId);
        if (existing) {
          // Clamp to the same max as AddonSelectionSchema (99) to prevent
          // submitting a quantity the server schema would reject
          next.set(addonId, { ...existing, quantity: Math.min(quantity, 99) });
        }
      }

      return {
        ...state,
        selectedAddons: next,
        addonsTotalPrice: computeAddonsTotal(next),
      };
    }

    case 'CLEAR_ADDONS': {
      return {
        ...state,
        selectedAddons: new Map(),
        addonsTotalPrice: 0,
      };
    }
  }
}

function computeAddonsTotal(addons: Map<string, SelectedAddon>): number {
  let total = 0;
  for (const addon of addons.values()) {
    total += addon.price * addon.quantity;
  }
  return total;
}

function parseDurationFromLabel(label: string): number {
  const match = label.match(/(\d+)/);
  return match ? parseInt(match[1]!, 10) : 0;
}

/**
 * Optional pre-resolved location id (UUID, not slug) used to seed
 * `selectedLocationId` synchronously on the very first render. Pass this when
 * the SSR loader has already resolved the user's preferred location (from URL
 * `?location=` or the `book:last-location:<tenantId>` cookie) so the picker
 * paints collapsed immediately instead of flashing both cards before a
 * post-mount effect snaps to the saved choice.
 */
export function useBookingReducer(initialLocationId?: string | null) {
  return useReducer(bookingReducer, undefined, () => {
    const base = createInitialState();
    if (initialLocationId) {
      return { ...base, selectedLocationId: initialLocationId };
    }
    return base;
  });
}
